// @flow
import type { Node } from 'react';
import React, { Component } from 'react';
import { Arrow, Manager, Popper, Target } from 'react-popper';
import Transition from 'react-transition-group/Transition';
import PropTypes from 'prop-types';
import styled, { type StyledComponent } from 'styled-components';

import { Color, DSTokenMap } from '@rover/kibble/styles';
import { onSpaceOrEnterKeyPress, onSpecifiedKeyPress } from '@rover/react-lib/utils/input';
import { isHoverable } from '@rover/utilities/userAgent';

import { PopoverEvent, PopoverPlacement } from './popover.constants';
import StyledPopover, { TRANSITION_DURATION } from './StyledPopover';
import TargetWrapper from './TargetWrapper';

export const MOUSE_LEAVE_DELAY = 100;

type RenderProp = ({ onClose: () => void }) => Node;

export type Props = {
  children: Node,
  className: string,
  content: Node | RenderProp,
  open?: boolean,
  placement: PopoverPlacement,
  event: PopoverEvent,
  backgroundColor: Color,
  borderColor: Color,
  display: 'inline' | 'inline-block' | 'block',
  showArrow: boolean,
  toggleOnFocusOrBlur?: boolean,
  toggleOnSpaceOrEnter?: boolean,
  onChange?: (boolean) => void,
};

type State = {
  open: boolean,
};

export type PopperObj = { popper: { getArrowStyle: Function } };

type PopoverArrowContextProps = {
  ...Props,
  innerRef: (?HTMLDivElement) => void,
  transitionState: string,
};

const PopoverArrowContext = (
  { innerRef, ...other }: PopoverArrowContextProps,
  { popper }: PopperObj
) => <StyledPopover arrowStyle={popper.getArrowStyle()} {...other} ref={innerRef} />;

PopoverArrowContext.contextTypes = {
  popper: PropTypes.object.isRequired,
};

export const ContentWrapper: StyledComponent<{}> = styled.div`
  background: rgba(0, 0, 0, 0);
  -webkit-font-smoothing: antialiased;
  /* this max-width helps prevent Popovers from being wider than the screen, which can trigger a resize loop */
  max-width: calc(100vw - 60px);
`;

export const StyledArrow: StyledComponent<{}> = styled(Arrow)`
  position: absolute;
`;

export default class Popover extends Component<Props, State> {
  removeListener: ?() => void;

  clearTimeout: () => void;

  timeout: ?TimeoutID;

  popover: ?HTMLElement;

  static defaultProps = {
    display: 'inline',
    className: '',
    event: PopoverEvent.HOVER,
    placement: PopoverPlacement.BOTTOM,
    backgroundColor: DSTokenMap.BACKGROUND_COLOR_PRIMARY,
    borderColor: DSTokenMap.BORDER_COLOR_PRIMARY,
    showArrow: true,
    toggleOnFocusOrBlur: false,
    toggleOnSpaceOrEnter: false,
  };

  state = {
    open: false,
  };

  componentDidUpdate() {
    if (!this.isOpen() && this.removeListener) {
      this.removeListener();
      this.removeListener = undefined;
    }
  }

  componentWillUnmount() {
    this.clearTimeout();
    if (this.removeListener) {
      this.removeListener();
    }
  }

  static Event = PopoverEvent;

  static Placement = PopoverPlacement;

  handleOpen = () => {
    const { event, onChange } = this.props;

    if (event !== PopoverEvent.NONE) {
      if (onChange) onChange(true);
      this.setState(() => ({
        open: true,
      }));
    }
  };

  handleClose = () => {
    const { event, onChange } = this.props;

    if (event !== PopoverEvent.NONE) {
      if (onChange) onChange(false);
      this.setState(() => ({
        open: false,
      }));
    }
  };

  handleToggle = () => {
    const { event, onChange } = this.props;

    if (event !== PopoverEvent.NONE) {
      this.setState((state) => {
        if (onChange) onChange(!state.open);
        return {
          open: !state.open,
        };
      });
    }
  };

  isOpen = () => (this.props.event === PopoverEvent.NONE ? this.props.open : this.state.open);

  clearTimeout = () => {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  };

  handleOutsideClick = (event: Event) => {
    if (this.popover && !this.popover.contains((event.target: any))) {
      this.handleClose();
    }
  };

  handleClick = () => {
    const { event } = this.props;
    if (event === PopoverEvent.CLICK || !isHoverable()) {
      this.handleToggle();

      if (!this.isOpen()) {
        setTimeout(() => {
          document.addEventListener('click', this.handleOutsideClick, false);
        }, 0);
        this.removeListener = () =>
          document.removeEventListener('click', this.handleOutsideClick, false);
      }
    }
  };

  handleKeyDown = (e: KeyboardEvent) => {
    // allow Esc to close the popover if open
    if (this.isOpen()) {
      onSpecifiedKeyPress(e, ['Escape'], this.handleToggle);
    }
  };

  handleMouseEnter = () => {
    const { event } = this.props;

    if (event === PopoverEvent.HOVER && isHoverable()) {
      this.clearTimeout();
      this.handleOpen();
    }
  };

  handleMouseLeave = () => {
    const { event } = this.props;

    if (event === PopoverEvent.HOVER && isHoverable()) {
      this.clearTimeout();
      this.timeout = setTimeout(() => {
        this.handleClose();
        this.timeout = undefined;
      }, MOUSE_LEAVE_DELAY);
    }
  };

  renderContent = () => {
    const { content } = this.props;

    if (typeof content === 'function') {
      return content({ onClose: this.handleClose });
    }
    return content;
  };

  render() {
    const { children, content, placement, event, open, display, ...other } = this.props;
    const eventHandlers: { [string]: any } = {
      onClick: this.handleClick,
      onKeyDown: this.handleKeyDown, // we need to use onKeyDown to catch Esc as onKeyPress doesn't detect it
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
    };
    if (this.props.toggleOnSpaceOrEnter) {
      eventHandlers.onKeyPress = (e: KeyboardEvent) => onSpaceOrEnterKeyPress(e, this.handleToggle);
    }
    if (this.props.toggleOnFocusOrBlur) {
      eventHandlers.onFocus = () => {
        if (!this.state.open) this.handleToggle();
      };
      eventHandlers.onBlur = () => {
        if (this.state.open) this.handleToggle();
      };
    }

    return (
      <Manager tag={display === 'inline' ? 'span' : 'div'}>
        <Target {...eventHandlers} component={TargetWrapper} event={event} display={display}>
          {children}
        </Target>
        <Transition in={this.isOpen()} timeout={TRANSITION_DURATION} unmountOnExit>
          {(state) => (
            <Popper
              placement={placement.toString()}
              modifiers={{
                offset: {
                  offset: -10,
                },
                computeStyle: {
                  gpuAcceleration: false,
                },
                preventOverflow: {
                  enabled: false,
                },
                hide: {
                  enabled: false,
                },
              }}
            >
              {({
                popperProps: { ref, 'data-x-out-of-boundaries': dataXOutOfBoundaries, ...rest },
              }) => {
                const isOutOfBoundaries = typeof dataXOutOfBoundaries !== 'undefined';

                return (
                  <PopoverArrowContext
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                    placement={placement.toString()}
                    transitionState={state}
                    innerRef={(p) => {
                      this.popover = p;
                      ref(p);
                    }}
                    isOutOfBoundaries={isOutOfBoundaries}
                    {...rest}
                    {...other}
                  >
                    <ContentWrapper>{this.renderContent()}</ContentWrapper>
                    <StyledArrow />
                  </PopoverArrowContext>
                );
              }}
            </Popper>
          )}
        </Transition>
      </Manager>
    );
  }
}

// If we export this directly on top when we instantiate the variable, styleguide would endup using
// this component's name as displayname in the styleguide example.
// see warning section for more info: https://react-styleguidist.js.org/docs/components.html#default-vs-named-exports
export { PopoverArrowContext };



// WEBPACK FOOTER //
// ../react-lib/src/components/callouts/Popover/Popover.jsx